import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ShippingCaseServiceResponse,
  FileType,
  SippingCasesRequest,
  SippingCasesUpdateRequest,
} from "./types";
import { AppThunk } from "../../store";
import {
  getShippingServicesApi,
  preloadFileByTypeApi,
  loadFileByTypeApi,
  createShippinCaseApi,
  updateShippinCaseApi,
} from "./api";
import { ActivityItem } from "../shippingView/types";
import { getErrorMessageFromResponse } from "@helpers/get-error-message";

type CreateShippingCaseType = {
  services: ShippingCaseServiceResponse | null;
  pendingServices: ActivityItem[] | null;
  addedServices: { [id: number]: number[] } | null;
  files: FileType[] | null;
  freeFiles: (FileType & { name?: string })[] | null;
  error: string | null;
  success: string | null;
};

const initialState: CreateShippingCaseType = {
  services: null,
  pendingServices: null,
  addedServices: null,
  files: null,
  freeFiles: [],
  error: "",
  success: "",
};

const createShipping = createSlice({
  name: "createShipping",
  initialState: initialState,
  reducers: {
    getServicesSuccess(
      state: CreateShippingCaseType,
      action: PayloadAction<ShippingCaseServiceResponse>,
    ) {
      state.services = action.payload;
      state.error = null;

      if (state.pendingServices) {
        const routes = Object.keys(state.services);
        const added: { [index: number]: number[] } = {};

        state.pendingServices.forEach((service) => {
          const routeIndex = routes.findIndex(
            (route) => route === service.route,
          );
          if (routeIndex !== -1) {
            if (!added[routeIndex]) added[routeIndex] = [];
            added[routeIndex].push(service.activity_id);
          }
        });

        state.addedServices = added;
        state.pendingServices = null;
      }
    },
    getServicesFailure(
      state: CreateShippingCaseType,
      action: PayloadAction<string>,
    ) {
      state.error = action.payload;
      state.services = null;
    },
    startFilePreload(
      state: CreateShippingCaseType,
      action: PayloadAction<string>,
    ) {
      const index = state.files?.findIndex(
        (file) => file.doc_type === action.payload,
      );

      if (index === undefined || index === -1) {
        if (state.files) {
          state.files.push({ doc_type: action.payload, loading: true });
        } else {
          state.files = [{ doc_type: action.payload, loading: true }];
        }
      } else if (state.files) {
        state.files[index].loading = true;
      }
    },
    successfullFilePreload(
      state: CreateShippingCaseType,
      action: PayloadAction<{
        size?: number;
        doc_type: string;
        path?: string;
        presigned_url?: string;
        original?: boolean;
        name?: string;
      }>,
    ) {
      const { doc_type, path, presigned_url, original } = action.payload;
      if (!state.files) {
        state.files = [
          {
            doc_type,
            path,
            presigned_url,
            original,
          },
        ];
      } else {
        const index = state.files.findIndex(
          (file) => file.doc_type === doc_type,
        );

        if (index !== -1) {
          state.files[index] = { ...state.files[index], ...action.payload };
        } else {
          state.files.push(action.payload);
        }
      }
    },
    removeFreeFile(
      state: CreateShippingCaseType,
      action: PayloadAction<number>,
    ) {
      state.freeFiles =
        state.freeFiles?.filter((_, i) => i !== action.payload) || [];
    },
    removeFile(state: CreateShippingCaseType, action: PayloadAction<string>) {
      state.files =
        state.files?.filter((doc) => doc.doc_type !== action.payload) || [];
    },
    addFreeFile(state: CreateShippingCaseType) {
      state.freeFiles?.push({ original: false });
    },
    updateFreeFiles(
      state: CreateShippingCaseType,
      action: PayloadAction<{
        file: FileType & { name?: string };
        index?: number;
      }>,
    ) {
      const { file, index } = action.payload;
      if (!state.freeFiles) {
        state.freeFiles = [file];
        return;
      }
      if (index !== undefined) {
        state.freeFiles[index] = file;
      } else {
        state.freeFiles.push(file);
      }
    },
    startFreeFilePreload(
      state: CreateShippingCaseType,
      action: PayloadAction<number>,
    ) {
      if (state.freeFiles) state.freeFiles[action.payload].loading = true;
    },
    successfullFreeFilePreload(
      state: CreateShippingCaseType,
      action: PayloadAction<{
        path: string;
        size: number;
        presigned_url: string;
        index: number;
      }>,
    ) {
      const { presigned_url, size, index, path } = action.payload;
      if (state.freeFiles) {
        state.freeFiles[index].presigned_url = presigned_url;
        state.freeFiles[index].path = path;
        state.freeFiles[index].size = size;
        state.freeFiles[index].loading = false;
      }
    },
    createShippingCaseSuccess(
      state: CreateShippingCaseType,
      action: PayloadAction<string>,
    ) {
      state.success = action.payload;
      state.files = null;
      state.freeFiles = [];
      state.addedServices = null;
    },
    createShippingCaseFailure(
      state: CreateShippingCaseType,
      action: PayloadAction<string>,
    ) {
      state.error = action.payload;
    },
    hideWarning(state: CreateShippingCaseType) {
      state.error = null;
      state.success = null;
    },
    cleanShippingCase(state: CreateShippingCaseType) {
      state.files = null;
      state.addedServices = null;
      state.freeFiles = [];
    },
    updateShippingServices(
      state: CreateShippingCaseType,
      action: PayloadAction<ActivityItem[]>,
    ) {
      if (!state.services) {
        state.pendingServices = action.payload;
        return;
      }

      const routes = Object.keys(state.services);
      const added: { [index: number]: number[] } = {};

      action.payload.forEach((service) => {
        const routeIndex = routes.findIndex((route) => route === service.route);
        if (routeIndex !== -1) {
          if (!added[routeIndex]) added[routeIndex] = [];
          added[routeIndex].push(service.activity_id);
        }
      });

      state.addedServices = added;
      state.pendingServices = null;
    },
  },
});

export const {
  getServicesSuccess,
  getServicesFailure,
  startFilePreload,
  updateFreeFiles,
  addFreeFile,
  hideWarning,
  removeFile,
  removeFreeFile,
  successfullFilePreload,
  startFreeFilePreload,
  successfullFreeFilePreload,
  createShippingCaseSuccess,
  createShippingCaseFailure,
  cleanShippingCase,
  updateShippingServices,
} = createShipping.actions;

export default createShipping.reducer;

export const fetchShippingServices =
  (params: { offer_id: number }): AppThunk =>
  async (dispatch) => {
    try {
      const services = await getShippingServicesApi(params);
      dispatch(getServicesSuccess(services));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getServicesFailure(error));
    }
  };

export const preloadFileByType =
  (params: { data: any; doc_type: string; offer_id: number }): AppThunk =>
  async (dispatch) => {
    try {
      const file = params.data.get("file") as File;
      dispatch(startFilePreload(params.doc_type));
      const response = await preloadFileByTypeApi({
        filename: file.name,
        doc_type: params.doc_type,
        offer_id: params.offer_id,
      });
      await loadFileByTypeApi({
        file,
        src: response.presigned_url as string,
      });
      dispatch(
        successfullFilePreload({
          size: file.size as number,
          doc_type: params.doc_type,
          path: response.path as string,
          presigned_url: response.presigned_url as string,
        }),
      );
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getServicesFailure(error));
    }
  };

export const preloadFreeFile =
  (params: { file: File; index: number; offer_id: number }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(startFreeFilePreload(params.index));
      const response = await preloadFileByTypeApi({
        filename: params.file.name,
        doc_type: null,
        offer_id: params.offer_id,
      });
      await loadFileByTypeApi({
        file: params.file,
        src: response.presigned_url as string,
      });
      dispatch(
        successfullFreeFilePreload({
          index: params.index,
          size: params.file.size as number,
          path: response.path as string,
          presigned_url: response.presigned_url as string,
        }),
      );
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getServicesFailure(error));
    }
  };

export const createShippingCase =
  (params: SippingCasesRequest, navigate: (url: string) => void): AppThunk =>
  async (dispatch) => {
    try {
      const data = await createShippinCaseApi(params);
      dispatch(
        createShippingCaseSuccess("Shipping case was created successfully"),
      );
      navigate(`/offers/${params.offer_id}/shipping-case/${data.id}`);
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(createShippingCaseFailure(error));
    }
  };

export const updateShippingCase =
  (
    params: SippingCasesUpdateRequest,
    navigate: (url: string) => void,
  ): AppThunk =>
  async (dispatch) => {
    try {
      const data = await updateShippinCaseApi(params);
      dispatch(
        createShippingCaseSuccess("Shipping case was updated successfully"),
      );
      navigate(`/offers/${params.offer_id}/shipping-case/${data.id}`);
    } catch {
      dispatch(createShippingCaseFailure("Shipping case update failed"));
    }
  };
